import { useEffect, useState } from "react";
import "./LandingPage.css";
import { connect } from "react-redux";
import {
  cleanAllRedux,
  cleanPreliminaryResultData,
  fetchUserWithAllAttributes,
  saveQueryParams,
  saveLanguage,
} from "../../../store/actions";
import chooseNextPath from "../../Auth/services/chooseNextPath";
import { useQuery } from "../hooks/useQuery";
import login_backend from "../../../core/apis/login_backend";
import Loading from "../../../components/loading/Loading";
import {
  redirectToPreliminaryResultsPage,
  redirectToTrialSurveyPage,
  redirectToWelcomeSurveyPage,
} from "../../../core/Routing/RoutesRedirects";
import { hasTrialResultToday } from "../../../utils/helpers/surveysHelpers/PreliminaryResultsHelper";
import { checkIfThereAreTrialValues } from "../../Surveys/services/trialSurvey.service";
import HeaderFirstVersion from "../../../components/header/HeaderFirstVersion";
import Footer from "../../../components/footer/Footer";
import DancingEllipses from "../../../components/dancingEllipses/DancingEllipses";
import { useResize } from "../hooks/useResize";

import Cookies from "universal-cookie";
import { cookiesOptions } from "../../../utils/helpers/cookiesHelpers/cookiesHelper";
import VioletSunsetDesktop from "../../../components/violetSunset/VioletSunsetDesktop.js";
import VioletSunsetTablet from "../../../components/violetSunset/VioletSunsetTablet.js";

/**
 * Functional Component: LandingPage
 * This is the violet landing page, the first interaction any user has with violet
 *
 * @param {Array} roles
 * @param {Object} currentUser
 * @param {Function} saveQueryParams
 * @param {Object} preliminaryResult
 * @param {Object} answerValues
 * @param {Function} fetchUserWithAllAttributes
 * @param {Function} cleanPreliminaryResultData
 * @param {String} language
 * @param {Function} saveLanguage
 * @returns {Element}
 * */
const LandingPage = ({
  roles,
  currentUser,
  saveQueryParams,
  preliminaryResult,
  answerValues,
  fetchUserWithAllAttributes,
  cleanPreliminaryResultData,
  saveLanguage,
}) => {
  const [display, setDisplay] = useState(false);

  const cookies = new Cookies();

  //const { t } = useTranslation();

  var slideIndex = 0;

  let query = useQuery();

  //this refetches the user to update user data that may have been changed in another session on another device
  useEffect(() => {
    const refetchUser = async () => {
      await fetchUserWithAllAttributes(currentUser.attributes.user_id);
    };

    if (currentUser) refetchUser();

    //eslint-disable-next-line
  }, []);

  //checks if theres a current user on redux, if it exists sends him inside the app with choose next path
  //it also gets query params
  useEffect(() => {
    const checkUserAndGetParams = async () => {
      if (roles.length > 0) {
        try {
          await login_backend.get("/sessionExpired");
          await chooseNextPath(currentUser);
        } catch (e) {
          console.error(e);
        }
        setDisplay(true);
      } else {
        let queryParams = {};

        if (query.get("r")) {
          queryParams.r = query.get("r");
        }

        if (query.get("toFill")) {
          queryParams.toFill = query.get("toFill");
        }

        saveQueryParams(queryParams);

        setDisplay(true);
      }
    };

    checkUserAndGetParams();

    // eslint-disable-next-line
  }, []);

  //if a person has answered the trial survey today then the preliminary results page becomes the landing page
  useEffect(() => {
    if (hasTrialResultToday({ preliminaryResult })) {
      redirectToPreliminaryResultsPage();
    } else {
      cleanPreliminaryResultData();
    }

    // eslint-disable-next-line
  }, []);

  //if the query params include the check survey param, checks if the user has an incomplete trial survey and sends him to
  //complete it
  useEffect(() => {
    if (query.get("checkSurvey")) {
      if (checkIfThereAreTrialValues(answerValues)) {
        redirectToTrialSurveyPage();
      } else if (answerValues.feel.feel_type) {
        redirectToWelcomeSurveyPage();
      }
    }
  });

  //detects if a language cookie is already in place and
  //if it is saves the pre determined language
  useEffect(() => {
    if (query.get("lang")) {
      saveLanguage(query.get("lang"));
      cookies.set("language", query.get("lang"), cookiesOptions);
    }

    //eslint-disable-next-line
  }, []);

  //detects the browser default language and if it is the first time
  //the user visits the website it saves that one as the predetermined language
  useEffect(() => {
    if (window.navigator.language === "pt" && !cookies.get("language")) {
      saveLanguage("pt-PT");
      cookies.set("language", "pt-PT", cookiesOptions);
    } else if (!cookies.get("language")) {
      saveLanguage("en-GB");
      cookies.set("language", "en-GB", cookiesOptions);
    }
  });

  useResize();

  //if the page can render shows the image slide
  useEffect(() => {
    if (display) showSlides();
    // eslint-disable-next-line
  }, [display]);

  //this handles the images animation on landing page right size
  const showSlides = () => {
    var i;
    var slides = document.getElementsByClassName("Artboard-102x-100-1");
    for (i = 0; i < slides.length; i++) slides[i].style.display = "none";

    slideIndex++;
    if (slideIndex > slides.length) slideIndex = 1;

    const nextSlide = slides[slideIndex - 1];
    if (nextSlide) nextSlide.style.display = "block";
    setTimeout(showSlides, 4000); // Change image every 2 seconds
  };

  const navigateToWaitlist = () => {
    window.location.href = "https://tijubu.com/violet-join-waitlist";
  };

  if (!display) return <Loading />;

  return (
    <div id="landing-page-container">
      <HeaderFirstVersion />

      <div className="top-container" style={{ position: "relative" }}>
        <DancingEllipses dontShowSmall={true} />
        {window.innerWidth < 1120 ? (
          <VioletSunsetTablet navigateToWaitlist={navigateToWaitlist} />
        ) : (
          <VioletSunsetDesktop navigateToWaitlist={navigateToWaitlist} />
        )}
      </div>
      <Footer />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    roles: state.roles,
    currentUser: state.currentUser,
    preliminaryResult: state.preliminaryResult,
    answerValues: state.answerValues,
    language: state.language,
  };
};

export default connect(mapStateToProps, {
  saveQueryParams: saveQueryParams,
  cleanRedux: cleanAllRedux,
  fetchUserWithAllAttributes,
  cleanPreliminaryResultData,
  saveLanguage,
})(LandingPage);
